import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';
import { lastValueFrom } from 'rxjs';

// models
import { Product, ProductParams } from '../../../planogram/models/product';
import { RotationData } from '../../models/rotationData';
import { UnitConverter } from '../../utils/unitConverter';

// services
import { ProductsService } from '../../services/products.service';
import { SessionService } from '../../services/session.service';
import { Utils } from '../../services/utils.service';
import { DefaultProductParams } from '../../models/defaultProductParams';
import { ISearchSettings, ISearchRotation } from '../../../product/models/models';

const containerWidth: number = 213, containerHeight: number = 168;

const BASE_SEARCH_FIELDS: Array<ISearchSettings> = [
	{ title: 'PRODUCT_CARD.SEARCH_BY_ARTICLE', selected: false, name: 'article' },
	{ title: 'PRODUCT_CARD.SEARCH_BY_BARCODE', selected: false, name: 'barcode' },
	{ title: 'PRODUCT_CARD.SEARCH_BY_NAME', selected: false, name: 'name' },
];

@Component({
    selector: 'app-rotate-products-modal',
    templateUrl: './rotate-products-modal.component.html',
    styleUrls: ['./rotate-products-modal.component.scss'],
    standalone: false
})
export class RotateProductsModalComponent implements OnInit {

	changedProducts: Array<RotationData> = [{
		rotate_from: '',
		rotate_to: '',
		product_rotate_from: null,
		product_rotate_to: null,
		errors: {}
	}];
	products: Array<Product> = [];
	product: Product;
	configureMode: boolean = false;

	defaultProductParams: DefaultProductParams = new DefaultProductParams();
	width: number;
	height: number;
	depth: number;

	previewWidth: number;
	previewHeight: number;
	uploadedImage: File;
	uploadedImageUrl: string;
	imageType: string;
	googleImages: Array<string> = [];
	googleIndex: number;
	googleLoader: boolean = false;
	isInitialProduct: boolean = true;
	isValidProduct: boolean = true;
	canEditProduct: {
		image: boolean,
		params: boolean,
		all: boolean
	};

	searchSettings: Array<ISearchRotation> = [];
	currentSearchValuesFrom: Array<string> = [];
	currentSearchValuesTo: Array<string> = [];

	@Output('openConfigureProductModal') openConfigureProductModalEvent: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('searchResults', { static: false }) searchResults: ElementRef;
	@ViewChild('addProductsContainer') addProductsContainer: ElementRef<HTMLElement>;

	constructor(
		private dialogRef: MatDialogRef<RotateProductsModalComponent>,
		private translate: TranslateService,
		private productsService: ProductsService,
		private sessionService: SessionService
	) {
		this.width = this.defaultProductParams.width;
		this.height = this.defaultProductParams.height;
		this.depth = this.defaultProductParams.depth;

		this.setPreviewSizes();
	}

	ngOnInit(): void {
		this.canEditProduct = {
			all: this.sessionService.hasAccess('ph-product-parameters-edit') || this.sessionService.hasAccess('ph-product-image-edit'),
			image: this.sessionService.hasAccess('ph-product-image-edit'),
			params: this.sessionService.hasAccess('ph-product-parameters-edit')
		};

		this.searchSettings.push(this.getDefaultSearchSettings());
		this.currentSearchValuesFrom.push('');
		this.currentSearchValuesTo.push('');
	}

	getDefaultSearchSettings(): ISearchRotation {
		return {
			searchFromSettings: BASE_SEARCH_FIELDS.map(item => ({ ...item })),
			searchToSettings: BASE_SEARCH_FIELDS.map(item => ({ ...item }))
		};
	}

	private scrollToResults(index: number): void {
		setTimeout((): void => {
			const element: HTMLElement = document.getElementById(`search-results-${index}`);
			if (element) {
				element.scrollIntoView({
					behavior: 'smooth',
					block: 'start'
				});
			}
		}, 0);
	}

	addNewInputs(): void {
		this.changedProducts.push({
			rotate_from: '',
			rotate_to: '',
			product_rotate_from: null,
			product_rotate_to: null,
			errors: {}
		});
		this.searchSettings.push(this.getDefaultSearchSettings());
		this.currentSearchValuesFrom.push('');
		this.currentSearchValuesTo.push('');
	}

	getSearchFromOptionsActive(index: number): boolean {
		const selectedLength: number = this.searchSettings[index].searchFromSettings.filter((option: ISearchSettings): boolean => option.selected).length;
		return selectedLength > 0;
	}

	getSearchToOptionsActive(index: number): boolean {
		const selectedLength: number = this.searchSettings[index].searchToSettings.filter((option: ISearchSettings): boolean => option.selected).length;
		return selectedLength > 0;
	}

	apply(): void {
		if (!this.disableButton()) this.dialogRef.close({ status: true, data: this.changedProducts});
	}

	checkDuplicates(product: any): void {
		if (
			product.product_rotate_from &&
			this.changedProducts.filter((p: any): boolean =>
				p.rotate_from && p.product_rotate_from.id == product.product_rotate_from.id
			).length == 1
		) {
			this.changedProducts
				.find((p: any): boolean => p.product_rotate_from.id == product.product_rotate_from.id)
				.errors.error_duplicate_rotate_from = false;
		}
	}

	latestSearchValues: { [key: string]: string } = {};

	async checkProductExist(value: string, idx: number, key: string): Promise<void> {
		const isFrom: boolean = key === 'rotate_from';
		const currentValue: string = isFrom ? this.currentSearchValuesFrom[idx] : this.currentSearchValuesTo[idx];

		const actualValue: string = value || currentValue;

		if (!actualValue) {
			this.changedProducts[idx][key] = '';
			this.changedProducts[idx].errors = {};
			delete this.changedProducts[idx]['product_' + key];
			return;
		}

		const requestKey = `${idx}_${key}`;
		this.latestSearchValues[requestKey] = actualValue;

		const searchSettings: Array<ISearchSettings> = isFrom
			? this.searchSettings[idx].searchFromSettings
			: this.searchSettings[idx].searchToSettings;

		const isAllUnselected: boolean = searchSettings.every((s: ISearchSettings): boolean => !s.selected);

		const searchFields: Array<keyof ProductParams> = searchSettings
			.filter((option: ISearchSettings): boolean => isAllUnselected || option.selected)
			.map((option: ISearchSettings): keyof ProductParams => option.name as keyof ProductParams);

		const products: Array<ProductParams> = await lastValueFrom(this.productsService.select([actualValue], searchFields));

		if (this.latestSearchValues[requestKey] !== actualValue) return;

		this.setChangedProductsObj(products, idx, key, actualValue);

		if (products.length) {
			this.scrollToResults(idx);
		}
	}

	close(): void {
		this.dialogRef.close({ status: false, data: [] });
	}

	disableButton(): boolean {
		return this.changedProducts
				.map((p: RotationData): boolean => Object.values(p.errors).some((value: boolean): boolean => value == true))
				.some((value: boolean): boolean => value) ||
			this.changedProducts
				.map((p: RotationData): Array<string> => [p.rotate_from, p.rotate_to])
				.flat()
				.some((val: string): boolean => !val.length) ||
			!this.changedProducts.filter((p: RotationData): string => p.rotate_from && p.rotate_to).length;
	}

	isInitialProductData(): boolean {
		return this.width == UnitConverter.getConvertValue(this.product.width) &&
			this.height == UnitConverter.getConvertValue(this.product.height) &&
			this.depth == UnitConverter.getConvertValue(this.product.depth) &&
			!this.imageType;
	}

	isValidProductData(): boolean {
		return this.depth > 0 && this.height > 0 && this.width > 0;
	}

	removeItem(index: number, product: any): void {
		this.changedProducts.splice(index, 1);
		this.checkDuplicates(product);
	}

	setChangedProductsObj(products: Array<ProductParams>, idx: number, key: string, article: string): void {
		if (products.length) {
			this.changedProducts[idx]['errors']['error_duplicate_' + key] = !!(key == 'rotate_from' &&
				this.changedProducts
					.filter((p: RotationData, i: number): boolean => i !== idx && p['product_rotate_from'] && p['product_rotate_from'].id == products[0].id).length);
			this.changedProducts[idx][key] = article;
			this.changedProducts[idx]['product_' + key] = this.setProductConf(products[0]);
			this.changedProducts[idx]['errors']['error_' + key] = false;
		} else {
			this.changedProducts[idx].errors['error_' + key] = !!article;
			this.changedProducts[idx]['errors']['error_duplicate_' + key] = false;
			this.changedProducts[idx]['product_' + key] = null;
		}
	}

	setPreviewSizes(): void {
		if (this.width / this.height > containerWidth / containerHeight) {
			this.previewWidth = containerWidth;
			this.previewHeight = Math.round(containerWidth * this.height / this.width);
		} else {
			this.previewHeight = containerHeight;
			this.previewWidth = Math.round(containerHeight * this.width / this.height);
		}
	}

	setProductConf(product: ProductParams): Product {
		const newProduct: Product = new Product(product);
		newProduct.depth = newProduct.depth || this.defaultProductParams.depth;
		newProduct.width = newProduct.width || this.defaultProductParams.width;
		newProduct.height = newProduct.height || this.defaultProductParams.height;
		newProduct.photo = newProduct.photo || `/static/img/${this.translate.currentLang}/img404.jpg`;

		return newProduct;
	}

	updateProductsConfigure(products: Array<any>): void {
		products.forEach((p: any): void => {
			if (p.product_rotate_from && p.product_rotate_from.id == this.product.id) {
				p.product_rotate_from.width = this.width;
				p.product_rotate_from.height = this.height;
				p.product_rotate_from.depth = this.depth;
			} else if (p.product_rotate_to.id && p.product_rotate_to.id == this.product.id) {
				p.product_rotate_to.width = this.width;
				p.product_rotate_to.height = this.height;
				p.product_rotate_to.depth = this.depth;
			}
		});
	}

	// edit product block
	async applyProductConfig(): Promise<any> {
		if (!this.isValidProduct || this.isInitialProduct) return;

		this.width = Utils.round(this.width);
		this.height = Utils.round(this.height);
		this.depth = Utils.round(this.depth);

		this.isValidProduct = this.isValidProductData();
		if (!this.width || !this.height || !this.depth) return;

		const products: Array<any> = this.changedProducts.filter((p: RotationData): boolean =>
				(p.product_rotate_from && p.product_rotate_from.id == this.product.id) ||
				(p.product_rotate_to && p.product_rotate_to.id == this.product.id)
			),
			updateObj: any = {};

		this.width = UnitConverter.getSaveConvertValue(this.width);
		this.height = UnitConverter.getSaveConvertValue(this.height);
		this.depth = UnitConverter.getSaveConvertValue(this.depth);
		if (this.width && this.width != this.product.width) updateObj.width = this.width;
		if (this.height && this.height != this.product.height) updateObj.height = this.height;
		if (this.depth && this.depth != this.product.depth) updateObj.depth = this.depth;

		if (this.imageType == 'google') {
			updateObj.photo = this.googleImages[this.googleIndex];

			products.forEach((p: any): void => {
				if (this.product.id == (p.product_rotate_from || {}).id) {
					p.product_rotate_from.photo = this.googleImages[this.googleIndex];
				} else {
					p.product_rotate_to.photo = this.googleImages[this.googleIndex];
				}
			});
		}

		if (this.imageType == 'file') {
			const reader: FileReader = new FileReader();

			reader.readAsDataURL(this.uploadedImage);
			reader.onload = async (): Promise<any> => {
				updateObj.photo = reader.result;

				products.forEach((p: any): void => {
					if (this.product.id == (p.product_rotate_from || {}).id) {
						p.product_rotate_from.photo = updateObj.photo;
					}
					if (this.product.id == (p.product_rotate_to || {}).id) {
						p.product_rotate_to.photo = updateObj.photo;
					}
				});

				this.configureMode = false;
				this.updateProductsConfigure(products);
				this.dialogRef.updateSize('800px', '570px');

				return await this.productsService.update(this.product.id, updateObj);
			};
		} else {
			this.configureMode = false;
			this.updateProductsConfigure(products);
			this.dialogRef.updateSize('800px', '570px');

			return await this.productsService.update(this.product.id, updateObj);
		}
	}

	changeProductConfigure(product: Product): void {
		this.imageType = '';
		this.dialogRef.updateSize('724px', 'auto');
		this.configureMode = true;
		this.product = product;
		this.width = UnitConverter.getConvertValue(product.width || this.defaultProductParams.width);
		this.depth = UnitConverter.getConvertValue(product.depth || this.defaultProductParams.depth);
		this.height = UnitConverter.getConvertValue(product.height || this.defaultProductParams.height);
		this.isInitialProduct = this.isInitialProductData();
		this.isValidProduct = this.isValidProductData();
	}

	chooseFile(files: Array<NgxFileDropEntry>): void {
		if (!files.length || !Utils.bruteCheckFileExtension(files[0].fileEntry.name)) return;

		(files[0].fileEntry as FileSystemFileEntry).file((file: File): void => {
			this.imageType = 'file';
			this.uploadedImage = file;
			this.uploadedImageUrl = URL.createObjectURL(file);
			this.isInitialProduct = this.isInitialProductData();
		});
	}

	closeProductConfig(): void {
		this.dialogRef.updateSize('800px', '570px');
		this.configureMode = false;
	}

	getPreviewImage(): string {
		return this.product.photo.split('/').slice(-1)[0] == 'img404.jpg' ?
			`/static/img/${this.translate.currentLang}/img404.jpg` :
			this.product.photo;
	}

	getProductSizes(): { width: number, height: number } {
		return Utils.getProductSizes(this.product, 100);
	}

	google(): void {
		this.googleLoader = true;
		this.productsService
			.googleImages(this.product.name, this.product.barcode)
			.then((result: Array<string>): void => {
				this.googleImages.length = 0;
				this.googleImages.push(...result);
				this.googleLoader = false;
				this.imageType = 'google';
				this.googleIndex = 0;
				this.isInitialProduct = this.isInitialProductData();
			});
	}

	googleNext(): void {
		if (this.googleIndex == this.googleImages.length - 1) {
			this.googleIndex = 0;
		} else {
			this.googleIndex++;
		}
	}

	googlePrev(): void {
		if (this.googleIndex == 0) {
			this.googleIndex = this.googleImages.length - 1;
		} else {
			this.googleIndex--;
		}
	}

	productDataChange(): void {
		this.isValidProduct = this.isValidProductData();
		this.isInitialProduct = this.isInitialProductData();
	}
}
